import * as React from 'react';
import AppTabs from '../../../components/AppTabs';
import './ExtrasSettings.scss'
import { FC, useState, useEffect, useMemo } from 'react';
import { Modal, ModalHeader, ModalBody, Form } from 'reactstrap';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import ButtonsContainer from '../../../components/ButtonsContainer';
import { AutocompleteTextField, NumberInputField, TextInputField, MultipleStockItemsField } from '../../../components/Forms/fields';
import { Row } from '../../../components/Forms/table/Row';
import { Col } from '../../../components/Forms/table/Column';
import { ExtraData, FeatureEnum } from '../../../models/api-models';
import Typography from '../../../components/Typography';
import { useGeneralSettings, useServicesSet } from '../../../store/settings/hooks';
import { serviceId, serviceName } from '../../../shared/appointmint/appointment';
import { useSelector } from 'react-redux';
import { getFeatures } from '../../../store/session/selectors';
import { Tab } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import InvoiceItemDetailsForm from '../../../components/Forms/InvoiceItemDetailsForm/InvoiceItemDetailsForm';
import { InfoBox } from '../../../components/UI/InfoBox';
import AppButton from '../../../components/AppButton';

export interface ExtraConfigurationModalProps {
    defaultValue?: ExtraData,
    onClose?: () => void,
    onSave: (value: ExtraData) => void,
    onDelete: (id: string) => void,
}

const ExtraConfigurationModal : FC<ExtraConfigurationModalProps> = ({
    defaultValue,
    onClose,
    onSave,
    onDelete,
}) => {

    const t = useTranslations();

    const allAppointmentTypes = useServicesSet();
    const generalSettings = useGeneralSettings();
    const features = useSelector(getFeatures);
    const hasInvoicesFeature = features.includes(FeatureEnum.Invoices);

    const [data, setData] = useState<ExtraData>(() => ({
        id: '',
        name: '',
        category: '',
        creationDate: '',
        appointmentTypes: [],
        priceForClient: 0,
        professionalPart: 0,
        stockItems: [],
        ...defaultValue,
        ...(defaultValue && { stockItems: defaultValue.stockItems || [] })
    }))
    const [tab, setTab] = React.useState<'general' | 'invoices'>('general');
    const [showServiceOptions, setShowServiceOptions] = useState(
        !!(defaultValue?.appointmentTypes?.length || defaultValue?.professionalPart)
    );
    const [showStockOptions, setShowStockOptions] = useState(
        !!(defaultValue?.stockItems?.length)
    );

    useEffect(() => {
        if (!defaultValue) return;
        setData({...defaultValue, stockItems: defaultValue.stockItems || []});
        setShowServiceOptions(!!(defaultValue.appointmentTypes?.length || defaultValue.professionalPart));
        setShowStockOptions(!!(defaultValue.stockItems?.length));
    }, [defaultValue]);

    const id = useMemo(() => `extra-${defaultValue?.id ?? 'new'}`, [defaultValue])

    return (
        <Modal size="lg" isOpen={!!defaultValue}>
            <ModalHeader>{t(TK.extraConfiguration)} - {data?.name || t(TK.new)}</ModalHeader>
            <ModalBody>
                { data &&
                    <>
                        <AppTabs value={tab} onChange={(_, v) => setTab(v)} indicatorColor="primary" textColor="primary" style={{ marginBottom: 'var(--spacer-S)', borderBottom: 'var(--border-thin) solid var(--appLigthGrey)' }}>
                            <Tab label={t(TK.general)} value="general" />
                            {hasInvoicesFeature && <Tab label={t(TK.invoiceData)} value="invoices" />}
                        </AppTabs>

                        <div>
                            {tab === 'general' && (
                                <Form style={{display: 'flex', marginTop: 'var(--spacer-S)', flexDirection: 'column', gap: 'var(--spacer-S)'}}>
                                    <Row>
                                        <Col md={8}>
                                            <TextInputField
                                                id={`${id}-name`}
                                                label={t(TK.name)}
                                                value={data.name}
                                                onChange={name => setData(d => ({...d, name}))}
                                            />
                                        </Col>
                                        <Col md={4}>
                                            <TextInputField
                                                id={`${id}-category`}
                                                label={t(TK.category)}
                                                value={data.category ?? ''}
                                                onChange={category => setData(d => ({...d, category}))}
                                            />
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md={4}>
                                            <NumberInputField
                                                id={`${id}-price-for-client`}
                                                label={t(TK.priceForClient)}
                                                append="€"
                                                value={data.priceForClient}
                                                onChange={(priceForClient = 0) => setData(d => ({...d, priceForClient}))}
                                            />
                                        </Col>
                                    </Row>
                                    <div style={{ border: 'var(--border-thin) solid var(--appLigthGrey)', borderRadius: 4 }}>
                                        <div
                                            onClick={() => setShowServiceOptions(v => !v)}
                                            style={{
                                                cursor: 'pointer',
                                                padding: 'var(--spacer-XS) var(--spacer-S)',
                                                backgroundColor: 'var(--bg-light)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 'var(--spacer-XS)',
                                                borderRadius: showServiceOptions ? '4px 4px 0 0' : 4,
                                            }}
                                        >
                                            {showServiceOptions ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                                            <span style={{ fontWeight: 600, fontSize: '0.9em' }}>{t(TK.extraOnlyForSpecificServices)}</span>
                                        </div>
                                        {showServiceOptions && (
                                            <div style={{ padding: 'var(--spacer-S)', display: 'flex', flexDirection: 'column', gap: 'var(--spacer-M)' }}>
                                                <Row>
                                                    <Col md={12}>
                                                        <AutocompleteTextField
                                                            id={`${id}-appointment-types`}
                                                            label={t(TK.availableFor)}
                                                            multiSelect
                                                            options={allAppointmentTypes.map(k => serviceName(k, generalSettings))}
                                                            values={data.appointmentTypes.map(k => serviceName(k, generalSettings))}
                                                            onValuesChanged={displayNames => setData(d => ({...d, appointmentTypes: displayNames.map(n => serviceId(n, generalSettings))}))}
                                                        />
                                                        <Typography size='smaller'>{t(TK.extraAvailableForNote)}</Typography>
                                                    </Col>
                                                </Row>
                                                <Row>
                                                    <Col md={4}>
                                                        <NumberInputField
                                                            id={`${id}-professional-part`}
                                                            label={t(TK.professionalProfit)}
                                                            append="€"
                                                            min={0}
                                                            value={data.professionalPart}
                                                            onChange={(professionalPart = 0) => setData(d => ({...d, professionalPart}))}
                                                        />
                                                    </Col>
                                                </Row>
                                            </div>
                                        )}
                                    </div>

                                    { features.includes(FeatureEnum.Stock) &&
                                        <div style={{ border: 'var(--border-thin) solid var(--appLigthGrey)', borderRadius: 4 }}>
                                            <div
                                                onClick={() => setShowStockOptions(v => !v)}
                                                style={{
                                                    cursor: 'pointer',
                                                    padding: 'var(--spacer-XS) var(--spacer-S)',
                                                    backgroundColor: 'var(--bg-light)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 'var(--spacer-XS)',
                                                    borderRadius: showStockOptions ? '4px 4px 0 0' : 4,
                                                }}
                                            >
                                                {showStockOptions ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                                                <span style={{ fontWeight: 600, fontSize: '0.9em' }}>{t(TK.extraRemoveFromStock)}</span>
                                            </div>
                                            {showStockOptions && (
                                                <div style={{ padding: 'var(--spacer-S)' }}>
                                                    <MultipleStockItemsField
                                                        id={`${id}-stock-items`}
                                                        label={t(TK.stock)}
                                                        values={data.stockItems || []}
                                                        onChange={stockItems => setData(d => ({...d, stockItems}))}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    }
                                </Form>
                            )}

                            {hasInvoicesFeature && tab === 'invoices' && (
                                <>
                                    <InfoBox>
                                        <small>{t(TK.invoiceDataDisclaimer)}</small>
                                    </InfoBox>
                                    <InvoiceItemDetailsForm
                                        id={`${id}-invoice-item-details`}
                                        value={data.invoiceItemDetails}
                                        onChange={invoiceItemDetails => setData(d => ({...d, invoiceItemDetails: invoiceItemDetails ?? undefined}))}
                                        disabled={false}
                                        parentContext="extra"
                                        parentName={data.name}
                                    />
                                </>
                            )}
                        </div>
                    </>
                }

                <hr/>

                <ButtonsContainer>
                    <AppButton id={`${id}-delete-button`} color="danger" onClick={() => onDelete(data.id)}>{t(TK.delete)}</AppButton>
                    <AppButton id={`${id}-save-button`} disabled={!data.name?.length} color="success" onClick={() => onSave(data)}>{t(TK.save)}</AppButton>
                    <AppButton id={`${id}-cancel-button`} onClick={onClose}>{t(TK.cancel)}</AppButton>
                </ButtonsContainer>
            </ModalBody>
        </Modal>
    );
}

export default ExtraConfigurationModal;